import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const teamMembers = [
  {
    id: 1,
    name: 'Joseph Mbitu',
    role: 'Managing Director',
    avatar: '/assets/images/avatars/joseph.jpg',
    description: 'With over 18 years of leadership in the financial services industry, Joseph Mbitu drives TGL Credit Limited’s strategic vision and growth. His expertise in corporate governance, strategic planning, and market expansion has positioned TGL as a leader in non-bank financial solutions. Joseph has spearheaded innovative product development, fostering partnerships that enhance client value and promote financial inclusion across Kenya.',
    social: {
      linkedin: 'https://linkedin.com/in/joseph-mbitu',
      twitter: '',
    },
  },
  {
    id: 2,
    name: 'Jackie Githiga',
    role: 'Relationship Manager',
    avatar: '/assets/images/avatars/jackie.jpg',
    description: 'Jackie Githiga, with 8 years of experience in client relationship management, excels in building and maintaining strong partnerships at TGL Credit Limited. Her expertise in understanding client needs and delivering tailored financial solutions has driven customer satisfaction and loyalty. Jackie’s proactive approach ensures seamless communication and exceptional service delivery for TGL’s diverse clientele.',
    social: {
      linkedin: 'https://linkedin.com/in/jackie-githiga',
      twitter: '',
    },
  },
  {
    id: 3,
    name: 'Ann Wangai',
    role: 'Human Resource Manager',
    avatar: '/assets/images/avatars/ann.jpg',
    description: 'Ann Wangai, with 4 years of experience in human resource management, leads TGL’s efforts in talent acquisition, employee development, and organizational culture. Her strategic HR initiatives have built a high-performing, motivated team aligned with TGL’s core values. Ann specializes in fostering inclusive workplaces and implementing training programs that enhance employee skills and engagement.',
    social: {
      linkedin: 'https://linkedin.com/in/ann-wangai',
      twitter: '',
    },
  },
  {
    id: 4,
    name: 'Abigael Kamau',
    role: 'Finance Manager',
    avatar: '/assets/images/avatars/abigael.jpg',
    description: 'Abigael Kamau brings 5 years of experience in financial management and strategic accounting to TGL Credit Limited. Her expertise in budgeting, financial forecasting, and risk management ensures the company’s fiscal health and compliance. Abigael has implemented robust financial systems that optimize resource allocation and support TGL’s mission to deliver accessible financial services.',
    social: {
      linkedin: 'https://linkedin.com/in/abigael-kamau',
      twitter: '',
    },
  },
];

const coreValues = [
  {
    title: 'Professionalism',
    description: 'Demonstrating professionalism in interactions with customers, partners, and colleagues, including maintaining a positive attitude, being punctual, and adhering to professional standards and etiquette.',
  },
  {
    title: 'Integrity',
    description: 'Upholding honesty, transparency, and ethical behavior in all business dealings. This includes being truthful in communications, honoring commitments, and adhering to legal and moral standards.',
  },
  {
    title: 'Customer Focus',
    description: 'Prioritizing customer satisfaction by delivering high-quality products or services, understanding and meeting customer needs, and providing excellent customer service.',
  },
  {
    title: 'Teamwork',
    description: 'Fostering collaboration, respect, and support among team members to achieve common goals and solve challenges effectively.',
  },
  {
    title: 'Excellence',
    description: 'Striving for excellence in everything the company does, from product/service quality to customer experiences, operational efficiency, and employee performance.',
  },
];

function AboutUs() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const openModal = (member) => {
    setSelectedMember(member);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMember(null);
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && isModalOpen) closeModal();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isModalOpen]);

  return (
    <div className="bg-gray-50 py-16 sm:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          className="relative bg-primary-blue text-white rounded-lg py-12 px-6 text-center mb-12 animate-fade-up"
          data-aos="fade-up"
        >
          <div
            className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{
              backgroundImage: `url('/assets/images/about-bg.jpg'), linear-gradient(#f3f4f6, #f3f4f6)`,
            }}
          ></div>
          <div className="relative z-10">
            <h1 className="text-2xl sm:text-3xl font-inter font-bold">
              About TGL Credit Limited
            </h1>
            <p className="text-sm font-inter font-normal mt-4 max-w-2xl mx-auto">
              Discover our mission, vision, and the dedicated team empowering your financial future
            </p>
          </div>
        </div>

        {/* Three-Column Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12 animate-fade-in" data-aos="fade-up" data-aos-delay="100">
          {/* Who We Are */}
          <div className="bg-primary-red text-white rounded-lg p-6">
            <h2 className="text-xl font-inter font-bold mb-2 text-center">Who We Are</h2>
            <hr className="border-t-2 border-white/20 w-24 mx-auto mb-6" />
            <p className="text-sm font-inter font-normal leading-relaxed">
              TGL is a leading non-bank financial Services Company registered in Kenya under the Companies Act (CAP 486) of the Laws of Kenya. TGL product offerings include Logbook Loans, Trade Finance and Insurance products. Professionally managed, TGL has a proven track record in providing highly effective solutions which are innovative, structured and customized to meet the clients’ needs.
            </p>
          </div>

          {/* Our Vision and Mission */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-inter font-bold text-primary-blue mb-2 text-center">Our Vision</h2>
            <p className="text-sm font-inter font-normal text-gray-600 leading-relaxed mb-6">
              To be the leading catalyst for inclusive financial empowerment for individuals and businesses in Kenya.
            </p>
            <hr className="border-t-2 border-primary-blue/20 w-24 mx-auto mb-6" />
            <h2 className="text-xl font-inter font-bold text-primary-blue mb-2 text-center">Our Mission</h2>
            <p className="text-sm font-inter font-normal text-gray-600 leading-relaxed">
              To promote economic growth of individuals and businesses by providing accessible and affordable financial services.
            </p>
          </div>

          {/* Core Values */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-inter font-bold text-primary-blue mb-2 text-center">Core Values</h2>
            <hr className="border-t-2 border-primary-blue/20 w-24 mx-auto mb-6" />
            <ul className="space-y-4">
              {coreValues.map((value, index) => (
                <li key={index} className="text-sm font-inter font-normal text-gray-600">
                  <h4 className="text-base font-inter font-semibold text-primary-blue mb-1">
                    {value.title}
                  </h4>
                  <p className="leading-relaxed">{value.description}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Team */}
   

        {/* Modal */}
        {isModalOpen && selectedMember && (
          <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 animate-fade-in"
            role="dialog"
            aria-label={`${selectedMember.name}'s profile modal`}
            data-aos="zoom-in"
          >
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-md mx-4 p-8 relative">
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 text-gray-600 hover:text-primary-red transition-colors focus:ring-2 focus:ring-primary-red focus:ring-offset-2"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="text-center">
                {selectedMember.avatar ? (
                  <img
                    src={selectedMember.avatar}
                    alt={selectedMember.name}
                    className="w-40 h-40 rounded-full object-cover mx-auto mb-4 shadow-lg"
                    loading="lazy"
                    onError={(e) => { e.target.src = '/assets/images/fallback.jpg'; }}
                  />
                ) : (
                  <div className="w-40 h-40 rounded-full bg-gradient-to-r from-primary-blue to-primary-red flex items-center justify-center text-white font-inter text-4xl mx-auto mb-4 shadow-lg">
                    {selectedMember.name.charAt(0)}
                  </div>
                )}
                <h3 className="text-base font-inter font-bold text-primary-blue mb-2">
                  {selectedMember.name}
                </h3>
                <p className="text-sm font-inter font-normal text-gray-600 mb-2">{selectedMember.role}</p>
                <p className="text-xs font-inter font-normal text-gray-600 mb-4">{selectedMember.description}</p>
                <div className="flex justify-center gap-4">
                  {selectedMember.social.linkedin && (
                    <a
                      href={selectedMember.social.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white text-primary-blue rounded-full p-2 transition-all duration-300 hover:bg-primary-red hover:text-white hover:scale-110 hover:shadow-primary-red/30 focus:ring-2 focus:ring-primary-red focus:ring-offset-2"
                      aria-label={`${selectedMember.name}'s LinkedIn profile`}
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 3a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14zm-1.5 15.25V13.5a3.25 3.25 0 00-3.25-3.25h-.01a3.25 3.25 0 00-3.24 3.25v4.75h-3v-10h3v1.45a3.74 3.74 0 013.24-1.95h-.01a3.75 3.75 0 013.75 3.75v5h-3zm-10-13.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm1.5 13.5v-10h-3v10h3z" />
                      </svg>
                    </a>
                  )}
                  {selectedMember.social.twitter && (
                    <a
                      href={selectedMember.social.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white text-primary-blue rounded-full p-2 transition-all duration-300 hover:bg-primary-red hover:text-white hover:scale-110 hover:shadow-primary-red/30 focus:ring-2 focus:ring-primary-red focus:ring-offset-2"
                      aria-label={`${selectedMember.name}'s Twitter profile`}
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div
          className="text-center animate-fade-up"
          style={{ animationDelay: '0.6s' }}
          data-aos="fade-up"
          data-aos-delay="400"
        >
          <Link
            to="/contact-us"
            className="inline-block bg-gradient-to-r from-primary-red to-primary-red-dark text-white font-inter font-medium px-8 py-4 rounded-lg hover:bg-primary-blue hover:scale-105 transition-all duration-300 text-sm uppercase tracking-wide focus:ring-2 focus:ring-primary-red focus:ring-offset-2"
            aria-label="Contact TGL Credit Limited"
          >
            Get in Touch
          </Link>
        </div>
      </div>
      <style>
        {`
          :root {
            --primary-blue: #2F498C;
            --primary-blue-dark: #1e355f;
            --primary-red: #843345;
            --primary-red-dark: #672737;
          }
          @keyframes fade-up {
            from { opacity: 0; transform: translateY(15px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-up {
            animation: fade-up 0.8s ease-in-out forwards;
          }
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .animate-fade-in {
            animation: fade-in 0.8s ease-in-out forwards;
          }
          .bg-primary-blue {
            background-color: var(--primary-blue);
          }
          .bg-primary-blue-dark {
            background-color: var(--primary-blue-dark);
          }
          .bg-primary-red {
            background-color: var(--primary-red);
          }
          .bg-primary-red-dark {
            background-color: var(--primary-red-dark);
          }
          .text-primary-blue {
            color: var(--primary-blue);
          }
          .text-primary-red {
            color: var(--primary-red);
          }
          .decoration-primary-red {
            text-decoration-color: var(--primary-red);
          }
          .hover\\:text-primary-red:hover {
            color: var(--primary-red);
          }
          .hover\\:bg-primary-blue:hover {
            background-color: var(--primary-blue);
          }
          .hover\\:bg-primary-red:hover {
            background-color: var(--primary-red);
          }
          .hover\\:text-primary-red-dark:hover {
            color: var(--primary-red-dark);
          }
          .hover\\:shadow-primary-red\\/30:hover {
            --tw-shadow-color: rgba(132, 51, 69, 0.3);
            --tw-shadow: var(--tw-shadow-colored);
          }
          .focus\\:ring-primary-blue:focus {
            --tw-ring-color: var(--primary-blue);
          }
          .focus\\:ring-primary-red:focus {
            --tw-ring-color: var(--primary-red);
          }
          .border-primary-blue\\/20 {
            border-color: rgba(47, 73, 140, 0.2);
          }
          .border-white\\/20 {
            border-color: rgba(255, 255, 255, 0.2);
          }
          @media (prefers-reduced-motion: reduce) {
            .animate-fade-up, .animate-fade-in, .hover\\:scale-105, .hover\\:scale-110, .hover\\:translate-y-2, .hover\\:shadow-primary-red\\/30 {
              animation: none !important;
              transform: none !important;
              opacity: 1 !important;
              transition: none !important;
            }
          }
        `}
      </style>
    </div>
  );
}

export default AboutUs;